import React from "react";
import {
  Input,
  Button,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  Icon,
  ScrollArea,
} from "@elaniintc/goes-design-system";
import {
  arrayConditionOptions,
  arrayFunctionOptions,
} from "../utils/constants";
import { cn } from "@/utils/theme";

interface ArrayOperationInputProps {
  /** Tipo de operación de array */
  operation: string;
  /** Elementos para gestionar los valores */
  elements: Array<{ id?: string; value?: string }>;
  /** Condición seleccionada para map/filter */
  selectedCondition?: string;
  /** Callback para actualizar elementos */
  onElementsChange: (elements: Array<{ id?: string; value?: string }>) => void;
  /** Callback para cambiar condición */
  onConditionChange?: (condition: string) => void;
  /** Grupo de elementos */
  group?:
    | "down-left"
    | "down"
    | "down-right"
    | "left"
    | "right"
    | "up"
    | "up-left"
    | "up-right"
    | "single"
    | "middle"
    | "middle-right"
    | "middle-left";
}

/**
 * Componente para renderizar inputs específicos de operaciones de array
 */
export const ArrayOperationInput: React.FC<ArrayOperationInputProps> = ({
  operation,
  elements,
  selectedCondition,
  onElementsChange,
  onConditionChange,
  group = "down-left",
}) => {
  const updateElement = (index: number, value: string) => {
    const newElements = [...elements];
    while (newElements.length <= index) {
      newElements.push({
        id: Math.random().toString(36).substr(2, 9),
        value: "",
      });
    }
    newElements[index] = { ...newElements[index], value };

    onElementsChange(newElements);
  };

  // Splice operation
  if (operation === "array_splice") {
    return (
      <>
        <Input
          className="w-full"
          group={group}
          placeholder="Escribe el índice del elemento"
          maxLength={255}
          value={elements[0]?.value || ""}
          onChange={(e) => {
            if (!/^\d*$/.test(e.target.value)) return; // allow just numbers
            updateElement(0, e.target.value);
          }}
        />
      </>
    );
  }

  // Trim operation (two inputs)
  if (operation === "array_trim") {
    return (
      <>
        <Input
          className="w-1/2"
          group={"down-left"}
          placeholder="Índice del primer elemento"
          maxLength={255}
          value={elements[0]?.value || ""}
          onChange={(e) => {
            if (!/^\d*$/.test(e.target.value)) return; // allow just numbers
            updateElement(0, e.target.value);
          }}
        />
        <Input
          className="w-1/2 "
          group={"down-right"}
          placeholder="Índice del último elemento"
          maxLength={255}
          value={elements[1]?.value || ""}
          onChange={(e) => {
            if (!/^\d*$/.test(e.target.value)) return; // allow just numbers
            updateElement(1, e.target.value);
          }}
        />
      </>
    );
  }

  // Chunk operation
  if (operation === "array_chunk") {
    return (
      <>
        <Input
          className="w-full"
          group={group}
          placeholder="Escribe el número de divisiones al realizar"
          maxLength={255}
          value={elements[0]?.value || ""}
          onChange={(e) => {
            if (!/^\d*$/.test(e.target.value)) return; // allow just numbers
            updateElement(0, e.target.value);
          }}
        />
      </>
    );
  }

  // IndexOf and filterLength operations
  if (operation === "array_index_of" || operation === "array_filter_length") {
    return (
      <>
        <Input
          className="w-full"
          group={group}
          placeholder="Escribe el texto a buscar"
          maxLength={255}
          value={elements[0]?.value || ""}
          onChange={(e) => {
            updateElement(0, e.target.value);
          }}
        />
      </>
    );
  }

  // Map and filter operations
  if (operation === "array_map" || operation === "array_filter") {
    return (
      <>
        <DropdownMenu>
          <DropdownMenuTrigger className="h-full" asChild>
            <Button
              className="h-full font-normal w-1/2 justify-between"
              variant="quiet"
              group={
                selectedCondition && operation === "array_map"
                  ? "middle-left"
                  : "down-left"
              }
            >
              <div className="flex items-center gap-2 truncate">
                <Icon name="filter" type="far" />
                {selectedCondition
                  ? arrayConditionOptions.find(
                      (c) => c.value === selectedCondition,
                    )?.label
                  : "Condición"}
              </div>
              <Icon name="angle-down" type="far" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="bottom" className="border-neutral">
            <ScrollArea className="h-[204px]">
              {arrayConditionOptions.map((option) => (
                <DropdownMenuItem
                  key={option.value}
                  onClick={() =>
                    onConditionChange && onConditionChange(option.value)
                  }
                >
                  <span className="text-body font-normal">{option.label}</span>
                </DropdownMenuItem>
              ))}
            </ScrollArea>
          </DropdownMenuContent>
        </DropdownMenu>
        <Input
          group={
            selectedCondition && operation === "array_map"
              ? "middle-right"
              : "down-right"
          }
          className="w-1/2 h-full"
          placeholder="Escribe un texto"
          maxLength={255}
          value={elements[0]?.value || ""}
          onChange={(e) => updateElement(0, e.target.value)}
        />
      </>
    );
  }

  return null;
};

export const ArrayFunctionSelector = ({
  selectedCondition,
  onConditionChange,
  elements,
  onElementsChange,
}: {
  selectedCondition?: string;
  onConditionChange?: (condition: string) => void;
  elements: Array<{ id?: string; value?: string }>;
  onElementsChange: (elements: Array<{ id?: string; value?: string }>) => void;
}) => {
  const shouldShowInputForFunction =
    selectedCondition === "prepend" ||
    selectedCondition === "append" ||
    selectedCondition === "replace";

  const updateElement = (index: number, value: string) => {
    const newElements = [...elements];
    while (newElements.length <= index) {
      newElements.push({
        id: Math.random().toString(36).substr(2, 9),
        value: "",
      });
    }
    newElements[index] = { ...newElements[index], value };

    onElementsChange(newElements);
  };

  // Handler para limpiar el input al cambiar de acción
  const handleConditionChange = (value: string) => {
    if (onConditionChange) onConditionChange(value);
    // Limpiar los inputs al cambiar de acción
    onElementsChange([]);
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild className="h-full">
          <Button
            className={cn(
              "font-normal justify-between",
              shouldShowInputForFunction ? "w-1/2" : "w-full",
            )}
            variant="quiet"
            group={shouldShowInputForFunction ? "down-left" : "down"}
          >
            <div className="flex items-center gap-2 truncate">
              <Icon name="function" type="far" />
              {selectedCondition
                ? arrayFunctionOptions.find(
                    (c) => c.value === selectedCondition,
                  )?.label
                : "Acción"}
            </div>
            <Icon name="angle-down" type="far" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="bottom" className="border-neutral">
          <ScrollArea className="h-[204px]">
            {arrayFunctionOptions.map((option) => (
              <DropdownMenuItem
                key={option.value}
                onClick={() => handleConditionChange(option.value)}
              >
                <div className="flex flex-col">
                  <span className="text-body font-semibold">
                    {option.label}
                  </span>
                  <span className="text-body-quiet font-mono">
                    {option.description}
                  </span>
                </div>
              </DropdownMenuItem>
            ))}
          </ScrollArea>
        </DropdownMenuContent>
      </DropdownMenu>
      {shouldShowInputForFunction && (
        <Input
          group="down-right"
          className="w-1/2 h-full"
          placeholder="Escribe un texto"
          maxLength={255}
          value={elements[1]?.value || ""}
          onChange={(e) => updateElement(1, e.target.value)}
        />
      )}
    </>
  );
};
